import type { ApiErrorKey, Lang } from '@app/shared';
import { translate, translateWith, type UiKey } from './dict';

/**
 * Перевод отказа API в текст экрана.
 *
 * Бэкенд отдаёт КЛЮЧ i18n и подстановки, а не готовую строку (AGENTS.md, правило 2;
 * ТЗ §19, расхождение №10). Этот файл — единственное место, где ключ превращается в текст.
 *
 * Главное требование: неизвестный ключ обязан быть ВИДЕН. Молчаливая замена на «что-то
 * пошло не так» скрыла бы и опечатку в коде ошибки, и целую ветку отказов, добавленную
 * на бэкенде позже: экран выглядел бы рабочим, а клиент не понимал бы, почему его не пускают.
 */

/** Ровно то, что кладёт в тело ответа `ApiError.toJSON()` из `@app/shared`. */
export interface ApiFailureLike {
  readonly key: string;
  readonly details?: Readonly<Record<string, string | number>> | undefined;
}

export interface DescribedError {
  /** Готовый текст для экрана. */
  readonly text: string;
  /** false, если ключ фронтенду неизвестен: такой отказ подлежит разбору, а не показу «как есть». */
  readonly known: boolean;
}

/**
 * Ключ ошибки бэкенда → ключ строки в словаре.
 *
 * Таблица ПОЛНАЯ по `ApiErrorKey` из `@app/shared` — это её главная работа. Добавь бэкенд
 * новый код ошибки, и `Record<ApiErrorKey, UiKey>` перестанет компилироваться здесь, а не
 * покажет клиенту «неизвестная ошибка» в проде. Никакого `Partial` и никакого фильтра
 * по префиксу `err.`: и то и другое молча пропустило бы пропущенный перевод.
 */
const BACKEND_ERROR_KEYS: Readonly<Record<ApiErrorKey, UiKey>> = {
  'err.globallyBanned': 'err.globallyBanned',
  'err.blacklisted': 'err.blacklisted',
  'err.tooManyActive': 'err.tooManyActive',
  'err.cooldown': 'err.cooldown',
  'err.slotTaken': 'err.slotTaken',
  'err.unauthorized': 'err.unauthorized',
  'err.initDataInvalid': 'err.initDataInvalid',
  'err.initDataExpired': 'err.initDataExpired',
  'err.forbidden': 'err.forbidden',
  'err.notFound': 'err.notFound',
  'err.validation': 'err.validation',
  'err.rateLimited': 'err.rateLimited',
  'err.slotOutsideWorkingHours': 'err.slotOutsideWorkingHours',
  'err.slotInPast': 'err.slotInPast',
  'err.businessNotPublished': 'err.businessNotPublished',
  'err.publishRequiresService': 'err.publishRequiresService',
  'err.publishRequiresStaff': 'err.publishRequiresStaff',
  'err.workingHoursInvalid': 'err.workingHoursInvalid',
  'err.workingHoursOverlap': 'err.workingHoursOverlap',
  'err.businessHasActiveAppointments': 'err.businessHasActiveAppointments',
  'err.businessArchived': 'err.businessArchived',
  'err.noShowTooEarly': 'err.noShowTooEarly',
  'err.noShowAlreadyForgiven': 'err.noShowAlreadyForgiven',
  'err.noShowAlreadyDisputed': 'err.noShowAlreadyDisputed',
  'err.noShowWrongStatus': 'err.noShowWrongStatus',
  'err.completeTooEarly': 'err.completeTooEarly',
  'err.proRequired': 'err.proRequired',
  'err.localBanExpired': 'err.localBanExpired',
  'err.timeOffRangeInvalid': 'err.timeOffRangeInvalid',
  'err.timeOffInPast': 'err.timeOffInPast',
  'err.internal': 'err.internal',
};

/** Отказы, которых у бэкенда нет: их придумывает транспорт (см. `api/http.ts`). */
const FRONTEND_ERROR_KEYS = ['err.network', 'err.unparseable', 'err.noInitData'] as const;

const KNOWN_ERROR_KEYS: ReadonlySet<string> = new Set<string>([
  ...Object.keys(BACKEND_ERROR_KEYS),
  ...FRONTEND_ERROR_KEYS,
]);

/** Для тестов и отладки: полный набор ключей, которые фронтенд умеет показать. */
export const TRANSLATABLE_ERROR_KEYS: readonly string[] = [...KNOWN_ERROR_KEYS];

/**
 * Отказы, которые означают «запись закрыта ограничением», а не «попробуйте иначе».
 * Их обрабатывает отдельная шторка «Запись недоступна» (ТЗ §14.2), потому что клиенту
 * нужны дата снятия и объяснение причины, а не строка в один абзац.
 */
export const RESTRICTION_ERROR_KEYS = ['err.globallyBanned', 'err.blacklisted'] as const;

export type RestrictionErrorKey = (typeof RESTRICTION_ERROR_KEYS)[number];

export function isRestrictionError(key: string): key is RestrictionErrorKey {
  return (RESTRICTION_ERROR_KEYS as readonly string[]).includes(key);
}

/** Целое из `details`: значение может прийти и числом, и строкой — форма деталей свободная. */
function intDetail(
  details: ApiFailureLike['details'],
  name: string,
): number | null {
  const raw = details?.[name];
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && /^-?\d+$/.test(raw)) return Number(raw);
  return null;
}

function stringDetail(details: ApiFailureLike['details'], name: string): string | null {
  const raw = details?.[name];
  if (typeof raw === 'string' && raw.length > 0) return raw;
  if (typeof raw === 'number') return String(raw);
  return null;
}

/**
 * Подстановки для конкретного ключа.
 *
 * `null` означает «известный ключ, но сервер не прислал того, без чего текст бессмыслен».
 * Пустой объект — «подстановки не нужны».
 */
function paramsFor(failure: ApiFailureLike): Record<string, string | number> | null {
  switch (failure.key) {
    case 'err.tooManyActive': {
      const limit = intDetail(failure.details, 'limit');
      return limit === null ? null : { limit };
    }
    case 'err.cooldown': {
      const waitSeconds = intDetail(failure.details, 'waitSeconds');
      if (waitSeconds === null) return null;
      // Округление ВВЕРХ и минимум одна минута: «подождите 0 мин» — это предложение
      // повторить прямо сейчас, а повтор сразу упрётся в тот же cooldown.
      return { minutes: Math.max(1, Math.ceil(waitSeconds / 60)) };
    }
    case 'err.validation': {
      const field = stringDetail(failure.details, 'field');
      return field === null ? null : { field };
    }
    case 'err.unparseable': {
      const status = intDetail(failure.details, 'status');
      return status === null ? null : { status };
    }
    default:
      return {};
  }
}

/**
 * Это отказ бэкенда с ключом? Нужен экранам, которые ведут себя по-разному в зависимости
 * от КОНКРЕТНОГО ключа, а не просто показывают текст (например, переспрашивают).
 */
export function isApiFailure(error: unknown): error is ApiFailureLike {
  return (
    typeof error === 'object' &&
    error !== null &&
    'key' in error &&
    typeof (error as { key: unknown }).key === 'string'
  );
}

export function describeApiError(lang: Lang, failure: ApiFailureLike): DescribedError {
  if (!KNOWN_ERROR_KEYS.has(failure.key)) {
    return {
      text: translateWith(lang, 'errUnknownKey', { key: failure.key }),
      known: false,
    };
  }

  const params = paramsFor(failure);
  if (params === null) {
    return {
      text: translateWith(lang, 'errMissingDetails', { key: failure.key }),
      known: false,
    };
  }

  const key = failure.key as UiKey;
  return {
    text:
      Object.keys(params).length > 0
        ? translateWith(lang, key, params)
        : translate(lang, key),
    known: true,
  };
}
